import { EButtonHoverType } from '@common/enums';

import type { ThemeButtonData, ThemeButtonHover } from '../entity/schemas';
import { ThemeColorNames } from '../enums/color-names.enum';
import { ThemeCssUnit } from '../enums/css.enum';
import { TRANSPARENT_COLOR } from '../config/colors.const';

/**
 * What a theme button starts as when the stored one carries no rest state.
 *
 * Every part of a theme is optional (`websiteThemePropsSchema`), so a button with a `styleType` and
 * nothing else parses — and `migrateWebsiteThemeButtonLegacy` deliberately preserves such buttons,
 * the ones that only ever had the deprecated `styles`/`data` bags. Both the settings panel and the
 * AI tools have to render one anyway: refusing would fail on exactly the themes that most need
 * fixing, and inventing the whole button would be worse. This is the middle: the shipped presets'
 * own rest state, in palette NAMES rather than literals, so it follows the site.
 */
export function defaultThemeButtonBase(): ThemeButtonData {
    return {
        color: [ThemeColorNames.light],
        background: [ThemeColorNames.primary],
        borderColor: [TRANSPARENT_COLOR],
        borderRadius: { value: 8, unit: ThemeCssUnit.px },
        borderStyle: 'solid',
        borderWidth: { value: 0, unit: ThemeCssUnit.px },
    };
}

/**
 * The hover state for a given kind, built from the rest state.
 *
 * Mirrors `sp-custom-button-v2`'s `changeHoverType` exactly, including the `color` kind's literals:
 * switching the effect rewrites the WHOLE hover state, because each kind has its own defaults and
 * the two that do not recolour must keep the button looking as it did.
 */
export function themeButtonHoverForType(hoverType: EButtonHoverType, base: ThemeButtonData): ThemeButtonHover {
    if (hoverType === EButtonHoverType.color) {
        return {
            hoverType,
            opacity: 100,
            shadow: 'none',
            color: ['rgb(255,255,255)'],
            background: ['rgb(22,117,242)'],
            borderColor: ['rgba(0,0,0,0)'],
        };
    }

    return {
        hoverType,
        opacity: hoverType === EButtonHoverType.opacity ? 80 : 100,
        shadow: hoverType === EButtonHoverType.shadow ? '0 0 10px 6px rgb(0 0 0 / 10%)' : 'none',
        color: base.color,
        background: base.background,
        borderColor: base.borderColor,
    };
}

/** The hover a button with no stored hover state gets — the same starting point the editor uses. */
export function defaultThemeButtonHover(base: ThemeButtonData): ThemeButtonHover {
    return themeButtonHoverForType(EButtonHoverType.opacity, base);
}
